/**
 * Shared state/actions provider for the Recommendation Engine coach views.
 * Holds the cross-tab state, the coach actions, the live-derived selectors and
 * the recalculating sequence. Driven either by the injected `on*` callbacks
 * (the real context, backed by tRPC) or by mock data
 * (`RecommendationEngineMockProvider`); payload types anchor on the router
 * inputs.
 */
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  COMPANY_PROFICIENCY_PRESETS,
  type CompanyProficiencyPreset,
} from '@/lib/recommendation/company-proficiency-presets'
import { buildGraphJsonSnapshotDiff } from '@/lib/recommendation/graph-json-audit'
import { clampProficiencyRange } from '@/lib/recommendation/proficiency-range'
import type { RouterInputs } from '@/lib/trpc'
import {
  applyKnowledgeMapSeed,
  normalizeSeedInput,
  persistedSeedStatesFromTopics,
  type SeedKnowledgeMapInput,
  type SeedKnowledgeMapPayload,
} from './_lib/apply-knowledge-map-seed'
import {
  classifyTopicBucket,
  computeBucketCounts,
  computeFitsIndicator,
  computeOverallMastery,
  estimatePlanDuration,
  type FitsIndicator,
  ingestionInputFromCompletedWork,
  isAttemptIngestibleForEngine,
  type PlanDurationEstimate,
  projectedFinishDate,
  rollupTopicProficiencyBounds,
  type TopicBucket,
} from './_lib/derivations'
import {
  buildSnapshotState,
  type DiffItem,
  deriveDirtyTabs,
  derivePendingDiff,
  type PendingDiffResult,
  type RecommendationItemSnapshot,
  type SnapshotState,
  summarizePendingDiffProcessing,
} from './_lib/pending-diff'
import {
  type ProcessingType,
  summarizePendingProcessing,
} from './_lib/processing-trigger-policy'
import type { SeedBucket } from './_lib/seed-buckets'
import { MOCK_CALCULATION_LOG } from './_mock-data/calculation-log'
import {
  buildCascadeEntry,
  type CascadeContext,
  type CascadeKind,
  deriveEntryStatus,
  makeCalculationLogEntry,
} from './_mock-data/cascades'
import { MOCK_COMPLETED_WORK } from './_mock-data/completed-work'
import { MOCK_RECOMMENDATIONS } from './_mock-data/recommendations'
import { MOCK_TOPICS } from './_mock-data/topics'
import {
  DEFAULT_BASELINE_EPOCH_AT,
  INITIAL_CALCULATION_LOG,
  INITIAL_COMPLETED_WORK,
  INITIAL_PLAN_CONFIG,
  INITIAL_RECOMMENDATIONS,
} from './constants'
import type {
  CalculationLogEntry,
  CalculationStatus,
  CompletedWorkItem,
  RecommendationItem,
  ReportedApproach,
  TopicState,
} from './types'

/** How long the "Recalculating…" state is shown before the cascade entry lands. */
const RECALC_DELAY_MS = 1200
function withTopicBounds(
  topic: TopicState,
  min: number,
  max: number,
): TopicState {
  const clamped = clampProficiencyRange(min, max)
  return {
    ...topic,
    minProficiencyThreshold: clamped.minProficiencyThreshold,
    maxProficiencyThreshold: clamped.maxProficiencyThreshold,
    subtopics: topic.subtopics.map((st) => ({
      ...st,
      minProficiencyThreshold: clamped.minProficiencyThreshold,
      maxProficiencyThreshold: clamped.maxProficiencyThreshold,
    })),
  }
}

function withRolledTopicBounds(
  topic: TopicState,
  subtopics: TopicState['subtopics'],
) {
  const rolledBounds = rollupTopicProficiencyBounds(subtopics, topic)
  const clampedBounds = clampProficiencyRange(
    rolledBounds.minProficiencyThreshold,
    rolledBounds.maxProficiencyThreshold,
  )
  return {
    ...topic,
    minProficiencyThreshold: clampedBounds.minProficiencyThreshold,
    maxProficiencyThreshold: clampedBounds.maxProficiencyThreshold,
    subtopics,
  }
}

function withUpdatedSubtopic(
  topic: TopicState,
  subtopicId: string,
  updater: (
    subtopic: TopicState['subtopics'][number],
  ) => TopicState['subtopics'][number],
): TopicState {
  const nextSubtopics = topic.subtopics.map((subtopic) =>
    subtopic.id === subtopicId ? updater(subtopic) : subtopic,
  )
  return withRolledTopicBounds(topic, nextSubtopics)
}

function areSubtopicRangesEqual(
  subtopic: TopicState['subtopics'][number],
  nextMin: number,
  nextMax: number,
): boolean {
  return (
    subtopic.minProficiencyThreshold === nextMin &&
    subtopic.maxProficiencyThreshold === nextMax
  )
}

function getSubtopicRange(
  topic: TopicState,
  subtopic: TopicState['subtopics'][number],
): { minProficiencyThreshold: number; maxProficiencyThreshold: number } {
  return {
    minProficiencyThreshold:
      subtopic.minProficiencyThreshold ?? topic.minProficiencyThreshold,
    maxProficiencyThreshold:
      subtopic.maxProficiencyThreshold ?? topic.maxProficiencyThreshold,
  }
}

function commitTopicBound(
  topic: TopicState,
  nextPartial: {
    minProficiencyThreshold?: number
    maxProficiencyThreshold?: number
  },
): TopicState | null {
  const clamped = clampProficiencyRange(
    nextPartial.minProficiencyThreshold ?? topic.minProficiencyThreshold,
    nextPartial.maxProficiencyThreshold ?? topic.maxProficiencyThreshold,
  )
  if (
    topic.minProficiencyThreshold === clamped.minProficiencyThreshold &&
    topic.maxProficiencyThreshold === clamped.maxProficiencyThreshold
  ) {
    return null
  }
  return withTopicBounds(
    topic,
    clamped.minProficiencyThreshold,
    clamped.maxProficiencyThreshold,
  )
}

function commitSubtopicBound(
  topic: TopicState,
  subtopic: TopicState['subtopics'][number],
  nextPartial: {
    minProficiencyThreshold?: number
    maxProficiencyThreshold?: number
  },
): { topic: TopicState; changed: boolean } {
  const currentRange = getSubtopicRange(topic, subtopic)
  const clamped = clampProficiencyRange(
    nextPartial.minProficiencyThreshold ?? currentRange.minProficiencyThreshold,
    nextPartial.maxProficiencyThreshold ?? currentRange.maxProficiencyThreshold,
  )
  if (
    areSubtopicRangesEqual(
      subtopic,
      clamped.minProficiencyThreshold,
      clamped.maxProficiencyThreshold,
    )
  ) {
    return { topic, changed: false }
  }
  return {
    topic: withUpdatedSubtopic(topic, subtopic.id, (row) => ({
      ...row,
      minProficiencyThreshold: clamped.minProficiencyThreshold,
      maxProficiencyThreshold: clamped.maxProficiencyThreshold,
    })),
    changed: true,
  }
}

interface PlanConfig {
  targetDate: string | null
  weeklyCommitmentHours: number
}

/**
 * One threshold save's payload — the server's input minus the client id the
 * real context supplies. Named once so the value interface, the provider prop
 * and the view's commit helper cannot drift apart.
 */
export type ProficiencyThresholdUpdate = Omit<
  RouterInputs['admin']['recommendationEngine']['updateProficiencyThresholds'],
  'clientId'
>

export interface RecommendationEngineValue {
  // --- state ---
  topics: TopicState[]
  planConfig: PlanConfig
  completedWork: CompletedWorkItem[]
  recommendations: RecommendationItem[]
  calculationLog: CalculationLogEntry[]
  recalculating: boolean
  engineEnabled: boolean
  knowledgeMapSeeded: boolean
  baselineEpochAt: Date | null
  baselineEpochTimeZone: string | null
  baselineEpochBusy: boolean
  pendingDiff: DiffItem[]
  dirtyTabs: string[]
  pendingProcessingTypes: ProcessingType[]
  pendingProcessingSummary: string
  canRevertToSnapshot: boolean
  catalogTopics?: RecommendationEngineCatalogTopic[]
  catalogTopicCount?: number
  catalogSubtopicCount?: number
  companyProficiencyPresets: CompanyProficiencyPreset[]
  proficiencyGoalsTopics?: TopicState[]
  lastAppliedSeedBucket: SeedBucket | null
  lastAppliedSeedAt: Date | null
  lastAppliedSeedCoachName: string | null
  hasPendingSeedProcessing: boolean
  persistedSeedSubtopicStates: Record<string, PersistedSeedSubtopicState>
  // --- derived ---
  topicsInPlanCount: number
  overallMastery: number
  bucketCounts: {
    mastered: number
    proficient: number
    learning: number
    remaining: number
  }
  planEstimate: PlanDurationEstimate | null
  projectedFinish: Date | null
  fitsIndicator: FitsIndicator
  bucketOf: (topicId: string) => TopicBucket
  /**
   * Derive "applied" vs "reverted" from the append-only stream. The entry
   * itself is never mutated — status is always computed from the existence
   * of a later `coach_revert` event pointing at it.
   */
  statusOf: (entryId: string) => CalculationStatus
  // --- actions ---
  toggleTopicInPlan: (topicId: string) => void
  toggleSubtopicInPlan: (topicId: string, subtopicId: string) => void
  setTopicMinThreshold: (topicId: string, value: number) => void
  setTopicMaxThreshold: (topicId: string, value: number) => void
  setSubtopicMinThreshold: (
    topicId: string,
    subtopicId: string,
    value: number,
  ) => void
  setSubtopicMaxThreshold: (
    topicId: string,
    subtopicId: string,
    value: number,
  ) => void
  setSubtopicRollupWeight: (
    topicId: string,
    subtopicId: string,
    weight: number,
  ) => void
  setWeeklyCommitment: (hours: number) => void
  setTargetDate: (date: string | null) => void
  applyCompanyPreset: (presetId: string) => void
  updateProficiencyThresholds?: (
    input: ProficiencyThresholdUpdate,
  ) => Promise<void>
  updateReportedApproach: (workId: string, approach: ReportedApproach) => void
  setRecommendationStatus: (
    recommendationId: string,
    status: RecommendationItemSnapshot['status'],
  ) => void
  markReviewed: (workId: string) => void
  saveGraphJsonEditorState?: (input: {
    adjacencyList: Record<string, string[]>
    meta: Record<
      string,
      {
        niche: number
        learning_order: number
      }
    >
  }) => Promise<void>
  /**
   * Seeds every subtopic from a bucket and optional per-subtopic overrides.
   * Emits one `coach_seed` calc-log entry whose `rowsAffected` lists every touched subtopic row plus
   * one topic rollup row per topic.
   */
  seedKnowledgeMap: (input: SeedKnowledgeMapInput) => Promise<void>
  /** Emits a `coach_revert` audit entry pointing at `entryId`. Does NOT mutate the original. */
  deleteCalculation: (entryId: string) => void
  /**
   * Emits an audit entry pointing at `entryId`:
   * - `coach_reapply` if the targeted entry is currently reverted
   * - `reprocess_cascade` (kind=`replay`) otherwise
   */
  redoCalculation: (entryId: string) => void
  setBaselineEpoch: (date: string) => Promise<void>
  updateBaselineEpoch: (date: string) => Promise<void>
  clearBaselineEpoch: () => Promise<void>
  triggerProcessingFromPendingChanges: () => void
  revertToLastSnapshot: () => void
}

export type {
  SeedKnowledgeMapInput,
  SeedKnowledgeMapPayload,
} from './_lib/apply-knowledge-map-seed'

export interface RecommendationEngineCatalogSubtopic {
  id: string
  slug: string
  name: string
  displayOrder: number
}

export interface RecommendationEngineCatalogTopic {
  id: string
  slug: string
  name: string
  displayOrder: number
  subtopics: RecommendationEngineCatalogSubtopic[]
}

export interface PersistedSeedSubtopicState {
  mastery: number
  stabilityDays: number
  difficulty: number
  isEnabled: boolean
}

export type SeedKnowledgeMapResult = {
  isReseed?: boolean
  hasPendingProcessing?: boolean
}

function normalizeBaselineEpoch(
  value: Date | string | null | undefined,
): Date | null {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const RecommendationEngineContext =
  createContext<RecommendationEngineValue | null>(null)

export function RecommendationEngineProvider({
  children,
  baselineEpochAt: baselineEpochAtProp = DEFAULT_BASELINE_EPOCH_AT,
  baselineEpochTimeZone = null,
  engineEnabled: engineEnabledProp = true,
  onSeedKnowledgeMap,
  initialKnowledgeMapSeeded = false,
  onSetBaselineEpoch,
  onUpdateBaselineEpoch,
  onClearBaselineEpoch,
  catalogTopics,
  catalogTopicCount,
  catalogSubtopicCount,
  companyProficiencyPresets = COMPANY_PROFICIENCY_PRESETS,
  proficiencyGoalsTopics,
  initialCompletedWork = INITIAL_COMPLETED_WORK,
  initialCalculationLog = INITIAL_CALCULATION_LOG,
  initialRecommendations = INITIAL_RECOMMENDATIONS,
  onUpdateProficiencyThresholds,
  initialSeedBucket = null,
  initialSeedOccurredAt = null,
  initialSeedCoachName = null,
  initialPendingSeedProcessing = false,
  onRunPendingProcessing,
  onSaveGraphJsonEditorState,
  onRevertGraphJsonEditorState,
  initialGraphJsonBaseline = null,
  initialGraphJsonCurrent = null,
  pendingGraphJsonAdjustmentId = null,
  initialPersistedSeedSubtopicStates = {},
}: {
  children: ReactNode
  baselineEpochAt?: Date | string | null
  baselineEpochTimeZone?: string | null
  engineEnabled?: boolean
  onSeedKnowledgeMap?: (
    input: SeedKnowledgeMapPayload,
  ) => Promise<SeedKnowledgeMapResult | undefined>
  initialKnowledgeMapSeeded?: boolean
  onSetBaselineEpoch?: (date: string) => Promise<void>
  onUpdateBaselineEpoch?: (date: string) => Promise<void>
  onClearBaselineEpoch?: () => Promise<void>
  catalogTopics?: RecommendationEngineCatalogTopic[]
  catalogTopicCount?: number
  catalogSubtopicCount?: number
  companyProficiencyPresets?: CompanyProficiencyPreset[]
  proficiencyGoalsTopics?: TopicState[]
  initialCompletedWork?: CompletedWorkItem[]
  initialCalculationLog?: CalculationLogEntry[]
  initialRecommendations?: RecommendationItem[]
  onUpdateProficiencyThresholds?: (
    input: ProficiencyThresholdUpdate,
  ) => Promise<void>
  initialSeedBucket?: SeedBucket | null
  initialSeedOccurredAt?: Date | string | null
  initialSeedCoachName?: string | null
  initialPendingSeedProcessing?: boolean
  onRunPendingProcessing?: () => Promise<void>
  onSaveGraphJsonEditorState?: (input: {
    adjacencyList: Record<string, string[]>
    meta: Record<
      string,
      {
        niche: number
        learning_order: number
      }
    >
  }) => Promise<void>
  onRevertGraphJsonEditorState?: (adjustmentId: string) => Promise<void>
  initialGraphJsonBaseline?: {
    adjacencyList: Record<string, string[]>
    meta: Record<
      string,
      {
        niche: number
        learning_order: number
      }
    >
  } | null
  initialGraphJsonCurrent?: {
    adjacencyList: Record<string, string[]>
    meta: Record<
      string,
      {
        niche: number
        learning_order: number
      }
    >
  } | null
  pendingGraphJsonAdjustmentId?: string | null
  initialPersistedSeedSubtopicStates?: Record<
    string,
    PersistedSeedSubtopicState
  >
}) {
  // Both are dependencies of the snapshot effect below, so their identity
  // must track their inputs, not the render count: a fresh array per render
  // re-fired the effect after every state update the effect itself made —
  // a self-sustaining render loop on mount (LG-619).
  const initialTopics = useMemo(
    () => proficiencyGoalsTopics ?? [],
    [proficiencyGoalsTopics],
  )
  const initialRecommendationSnapshots = useMemo<RecommendationItemSnapshot[]>(
    () =>
      initialRecommendations.map((rec) => ({
        id: rec.id,
        status: rec.status,
      })),
    [initialRecommendations],
  )
  const [topics, setTopics] = useState<TopicState[]>(() => initialTopics)
  const [planConfig, setPlanConfig] = useState<PlanConfig>({
    targetDate: INITIAL_PLAN_CONFIG.targetDate,
    weeklyCommitmentHours: INITIAL_PLAN_CONFIG.weeklyCommitmentHours,
  })
  const [completedWork, setCompletedWork] =
    useState<CompletedWorkItem[]>(initialCompletedWork)
  const [recommendations, setRecommendations] = useState<RecommendationItem[]>(
    initialRecommendations,
  )
  const [calculationLog, setCalculationLog] = useState<CalculationLogEntry[]>(
    initialCalculationLog,
  )
  const [recalculating, setRecalculating] = useState(false)
  const [knowledgeMapSeeded, setKnowledgeMapSeeded] = useState(
    initialKnowledgeMapSeeded,
  )
  const [baselineEpochAt, setBaselineEpochAtState] = useState<Date | null>(() =>
    normalizeBaselineEpoch(baselineEpochAtProp),
  )
  const [baselineEpochBusy, setBaselineEpochBusy] = useState(false)
  const [recommendationSnapshots, setRecommendationSnapshots] = useState<
    RecommendationItemSnapshot[]
  >(() => structuredClone(initialRecommendationSnapshots))
  const [lastAppliedSeedBucket, setLastAppliedSeedBucket] =
    useState<SeedBucket | null>(initialSeedBucket)
  const [lastAppliedSeedAt, setLastAppliedSeedAt] = useState<Date | null>(() =>
    normalizeBaselineEpoch(initialSeedOccurredAt),
  )
  const [lastAppliedSeedCoachName, setLastAppliedSeedCoachName] = useState<
    string | null
  >(initialSeedCoachName)
  const [hasPendingSeedProcessing, setHasPendingSeedProcessing] = useState(
    initialPendingSeedProcessing,
  )
  const [persistedSeedSubtopicStates, setPersistedSeedSubtopicStates] =
    useState<Record<string, PersistedSeedSubtopicState>>(
      initialPersistedSeedSubtopicStates,
    )
  const [graphJsonSnapshot, setGraphJsonSnapshot] = useState<{
    adjacencyList: Record<string, string[]>
    meta: Record<
      string,
      {
        niche: number
        learning_order: number
      }
    >
  } | null>(() =>
    initialGraphJsonCurrent ? structuredClone(initialGraphJsonCurrent) : null,
  )
  const [settingsSnapshot, setSettingsSnapshot] =
    useState<SnapshotState | null>(() =>
      buildSnapshotState({
        topics: initialTopics,
        plan: {
          weeklyCommitmentHours: INITIAL_PLAN_CONFIG.weeklyCommitmentHours,
          targetDate: INITIAL_PLAN_CONFIG.targetDate,
        },
        completedWork: initialCompletedWork,
        recommendations: initialRecommendationSnapshots,
        graphJson: initialGraphJsonBaseline
          ? structuredClone(initialGraphJsonBaseline)
          : null,
      }),
    )
  const pendingGraphJsonAdjustmentIdRef = useRef(pendingGraphJsonAdjustmentId)
  const recalcTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Cascades queued during the current recalculating window — flushed together. */
  const pendingCascades = useRef<{ kind: CascadeKind; ctx: CascadeContext }[]>(
    [],
  )
  /** Mirrors latest `topics` for synchronous seed apply (avoids stale closure). */
  const topicsRef = useRef<TopicState[]>(initialTopics)
  useEffect(() => {
    topicsRef.current = topics
  }, [topics])
  useEffect(() => {
    setBaselineEpochAtState(normalizeBaselineEpoch(baselineEpochAtProp))
  }, [baselineEpochAtProp])
  useEffect(() => {
    setKnowledgeMapSeeded(initialKnowledgeMapSeeded)
  }, [initialKnowledgeMapSeeded])
  useEffect(() => {
    if (proficiencyGoalsTopics !== undefined) {
      setTopics(proficiencyGoalsTopics)
    }
  }, [proficiencyGoalsTopics])
  useEffect(() => {
    setLastAppliedSeedBucket(initialSeedBucket)
  }, [initialSeedBucket])
  useEffect(() => {
    setLastAppliedSeedAt(normalizeBaselineEpoch(initialSeedOccurredAt))
  }, [initialSeedOccurredAt])
  useEffect(() => {
    pendingGraphJsonAdjustmentIdRef.current = pendingGraphJsonAdjustmentId
  }, [pendingGraphJsonAdjustmentId])
  useEffect(() => {
    if (initialGraphJsonCurrent) {
      setGraphJsonSnapshot(structuredClone(initialGraphJsonCurrent))
    }
    if (initialGraphJsonBaseline !== undefined) {
      setSettingsSnapshot((prev) => {
        const base =
          prev ??
          buildSnapshotState({
            topics: initialTopics,
            plan: {
              weeklyCommitmentHours: INITIAL_PLAN_CONFIG.weeklyCommitmentHours,
              targetDate: INITIAL_PLAN_CONFIG.targetDate,
            },
            completedWork: initialCompletedWork,
            recommendations: initialRecommendationSnapshots,
            graphJson: null,
          })
        return {
          ...base,
          graphJson: initialGraphJsonBaseline
            ? structuredClone(initialGraphJsonBaseline)
            : null,
        }
      })
    }
  }, [
    initialGraphJsonBaseline,
    initialGraphJsonCurrent,
    initialCompletedWork,
    initialRecommendationSnapshots,
    initialTopics,
  ])
  useEffect(() => {
    setLastAppliedSeedCoachName(initialSeedCoachName)
  }, [initialSeedCoachName])
  useEffect(() => {
    setCalculationLog(initialCalculationLog)
  }, [initialCalculationLog])
  useEffect(() => {
    setHasPendingSeedProcessing(initialPendingSeedProcessing)
  }, [initialPendingSeedProcessing])
  useEffect(() => {
    if (Object.keys(initialPersistedSeedSubtopicStates).length === 0) return
    setPersistedSeedSubtopicStates(initialPersistedSeedSubtopicStates)
  }, [initialPersistedSeedSubtopicStates])

  const engineEnabled =
    engineEnabledProp && baselineEpochAt !== null && baselineEpochBusy === false

  /**
   * Shared recalculating sequence: queue the cascade, flip the flag, and
   * (re)schedule a flush. Every queued cascade is appended when the timer
   * fires — rapid successive actions each get their own log entry.
   */
  const runRecalculation = useCallback(
    (kind: CascadeKind, ctx: CascadeContext) => {
      pendingCascades.current.push({ kind, ctx })
      setRecalculating(true)
      if (recalcTimer.current) clearTimeout(recalcTimer.current)
      recalcTimer.current = setTimeout(() => {
        const queued = pendingCascades.current
        pendingCascades.current = []
        recalcTimer.current = null
        const newEntries = queued.map((q) =>
          makeCalculationLogEntry(buildCascadeEntry(q.kind, q.ctx)),
        )
        // newest-fired action first
        newEntries.reverse()
        setCalculationLog((prev) => [...newEntries, ...prev])
        setRecalculating(false)
      }, RECALC_DELAY_MS)
    },
    [],
  )

  /**
   * Audit-event path. Coach_revert / coach_reapply / reprocess_cascade are
   * append-only records, not cascading recomputations — emit them
   * synchronously without the "Recalculating…" banner. Production parity:
   * a single INSERT into `CoachAdjustmentLog`, no engine work.
   */
  const emitAuditEntry = useCallback(
    (kind: CascadeKind, ctx: CascadeContext) => {
      const entry = makeCalculationLogEntry(buildCascadeEntry(kind, ctx))
      setCalculationLog((prev) => [entry, ...prev])
    },
    [],
  )

  // Clear any pending recalc timer if the provider unmounts (panel collapsed).
  useEffect(
    () => () => {
      if (recalcTimer.current) clearTimeout(recalcTimer.current)
    },
    [],
  )

  // --- actions ---
  const toggleTopicInPlan = useCallback(
    (topicId: string) => {
      if (!engineEnabled) return
      const subject = topics.find((t) => t.id === topicId)?.name ?? topicId
      setTopics((prev) =>
        prev.map((t) =>
          t.id === topicId ? { ...t, includedInPlan: !t.includedInPlan } : t,
        ),
      )
      runRecalculation('topic_inclusion_change', { actor: 'Coach', subject })
    },
    [engineEnabled, topics, runRecalculation],
  )

  const toggleSubtopicInPlan = useCallback(
    (topicId: string, subtopicId: string) => {
      if (!engineEnabled) return

      let subject: string | null = null
      setTopics((prev) => {
        const topic = prev.find((t) => t.id === topicId)
        if (!topic) return prev
        const subtopic = topic.subtopics.find((st) => st.id === subtopicId)
        if (!subtopic) return prev

        subject = subtopic.name
        return prev.map((t) =>
          t.id === topicId
            ? {
                ...t,
                subtopics: t.subtopics.map((st) =>
                  st.id === subtopicId
                    ? { ...st, isEnabled: st.isEnabled === false }
                    : st,
                ),
              }
            : t,
        )
      })

      if (subject) {
        runRecalculation('subtopic_enable_toggle', {
          actor: 'Coach',
          subject,
        })
      }
    },
    [engineEnabled, runRecalculation],
  )

  const setTopicMinThreshold = useCallback(
    (topicId: string, value: number) => {
      if (!engineEnabled) return
      const topic = topics.find((t) => t.id === topicId)
      if (!topic) return
      const nextTopic = commitTopicBound(topic, {
        minProficiencyThreshold: value,
      })
      if (!nextTopic) return
      setTopics((prev) => prev.map((t) => (t.id === topicId ? nextTopic : t)))
      runRecalculation('threshold_change', {
        actor: 'Coach',
        subject: topic.name,
      })
    },
    [engineEnabled, topics, runRecalculation],
  )

  const setTopicMaxThreshold = useCallback(
    (topicId: string, value: number) => {
      if (!engineEnabled) return
      const topic = topics.find((t) => t.id === topicId)
      if (!topic) return
      const nextTopic = commitTopicBound(topic, {
        maxProficiencyThreshold: value,
      })
      if (!nextTopic) return
      setTopics((prev) => prev.map((t) => (t.id === topicId ? nextTopic : t)))
      runRecalculation('threshold_change', {
        actor: 'Coach',
        subject: topic.name,
      })
    },
    [engineEnabled, topics, runRecalculation],
  )

  const setSubtopicMinThreshold = useCallback(
    (topicId: string, subtopicId: string, value: number) => {
      if (!engineEnabled) return
      const topic = topics.find((t) => t.id === topicId)
      const subtopic = topic?.subtopics.find((st) => st.id === subtopicId)
      if (!topic || !subtopic) return
      const commit = commitSubtopicBound(topic, subtopic, {
        minProficiencyThreshold: value,
      })
      if (!commit.changed) return
      setTopics((prev) =>
        prev.map((t) => (t.id === topicId ? commit.topic : t)),
      )
      runRecalculation('subtopic_threshold_change', {
        actor: 'Coach',
        subject: subtopic.name,
      })
    },
    [engineEnabled, topics, runRecalculation],
  )

  const setSubtopicMaxThreshold = useCallback(
    (topicId: string, subtopicId: string, value: number) => {
      if (!engineEnabled) return
      const topic = topics.find((t) => t.id === topicId)
      const subtopic = topic?.subtopics.find((st) => st.id === subtopicId)
      if (!topic || !subtopic) return
      const commit = commitSubtopicBound(topic, subtopic, {
        maxProficiencyThreshold: value,
      })
      if (!commit.changed) return
      setTopics((prev) =>
        prev.map((t) => (t.id === topicId ? commit.topic : t)),
      )
      runRecalculation('subtopic_threshold_change', {
        actor: 'Coach',
        subject: subtopic.name,
      })
    },
    [engineEnabled, topics, runRecalculation],
  )

  const setSubtopicRollupWeight = useCallback(
    (topicId: string, subtopicId: string, weight: number) => {
      if (!engineEnabled) return
      if (weight <= 0) return
      const topic = topics.find((t) => t.id === topicId)
      const subtopic = topic?.subtopics.find((st) => st.id === subtopicId)
      if (!topic || !subtopic) return
      if ((subtopic.rollupWeight ?? 1) === weight) return
      const nextTopic = withUpdatedSubtopic(topic, subtopicId, (row) => ({
        ...row,
        rollupWeight: weight,
      }))
      setTopics((prev) => prev.map((t) => (t.id === topicId ? nextTopic : t)))
      runRecalculation('rollup_weight_change', {
        actor: 'Coach',
        subject: subtopic.name,
      })
    },
    [engineEnabled, topics, runRecalculation],
  )

  const setWeeklyCommitment = useCallback(
    (hours: number) => {
      if (!engineEnabled) return
      setPlanConfig((prev) => ({ ...prev, weeklyCommitmentHours: hours }))
      // Minor setting — projection-only; append audit without recalc banner
      // (`reprocess-registry.ts` class `minor`).
      emitAuditEntry('weekly_commitment_change', {
        actor: 'Coach',
        subject: String(hours),
      })
    },
    [emitAuditEntry, engineEnabled],
  )

  /** Target-date changes do NOT recalculate — they only move the deadline comparison. */
  const setTargetDate = useCallback(
    (date: string | null) => {
      if (!engineEnabled) return
      setPlanConfig((prev) => ({ ...prev, targetDate: date }))
    },
    [engineEnabled],
  )

  const applyCompanyPreset = useCallback(
    (presetId: string) => {
      if (!engineEnabled) return
      const preset = companyProficiencyPresets.find((p) => p.id === presetId)
      if (!preset) return
      setTopics((prev) =>
        prev.map((topic) =>
          withTopicBounds(
            topic,
            preset.minProficiencyThreshold,
            preset.maxProficiencyThreshold,
          ),
        ),
      )
      runRecalculation('preset_applied', {
        actor: 'Coach',
        subject: preset.name,
      })
    },
    [companyProficiencyPresets, engineEnabled, runRecalculation],
  )

  const updateReportedApproach = useCallback(
    (workId: string, approach: ReportedApproach) => {
      if (!engineEnabled) return
      setCompletedWork((prev) =>
        prev.map((w) =>
          w.id === workId ? { ...w, reportedApproach: approach } : w,
        ),
      )
    },
    [engineEnabled],
  )

  const setRecommendationStatus = useCallback(
    (
      recommendationId: string,
      status: RecommendationItemSnapshot['status'],
    ) => {
      if (!engineEnabled) return
      setRecommendationSnapshots((prev) =>
        prev.map((item) =>
          item.id === recommendationId ? { ...item, status } : item,
        ),
      )
      setRecommendations((prev) =>
        prev.map((item) =>
          item.id === recommendationId ? { ...item, status } : item,
        ),
      )
    },
    [engineEnabled],
  )

  const markReviewed = useCallback(
    (workId: string) => {
      if (!engineEnabled) return
      const work = completedWork.find((w) => w.id === workId)
      if (!work) return
      const subject = work.subtopicName
      const ingestible = isAttemptIngestibleForEngine(
        ingestionInputFromCompletedWork(work, {
          knowledgeMapSeeded,
          baselineEpochAt,
        }),
      )
      setCompletedWork((prev) =>
        prev.map((w) => (w.id === workId ? { ...w, needsGrading: false } : w)),
      )
      if (ingestible.eligible) {
        runRecalculation('attempt_reviewed', { actor: 'Coach', subject })
      }
    },
    [
      engineEnabled,
      completedWork,
      knowledgeMapSeeded,
      baselineEpochAt,
      runRecalculation,
    ],
  )

  const saveGraphJsonEditorState = useCallback(
    async (input: {
      adjacencyList: Record<string, string[]>
      meta: Record<
        string,
        {
          niche: number
          learning_order: number
        }
      >
    }) => {
      const previousGraphJson = graphJsonSnapshot ?? settingsSnapshot?.graphJson
      await onSaveGraphJsonEditorState?.(input)
      setGraphJsonSnapshot(structuredClone(input))
      setCalculationLog((prev) => [
        {
          id: `calc_${crypto.randomUUID()}`,
          occurredAt: new Date().toISOString(),
          source: 'coach_adjustment',
          kind: 'original',
          replacesEntryIds: [],
          sourceTable: 'CoachAdjustmentLog',
          sourceRowId: `CoachAdjustmentLog:graph_json_saved_${crypto.randomUUID()}`,
          actorKind: 'coach',
          actorName: 'Coach',
          reasonCode: 'graph_json_saved',
          rationale:
            'Coach saved global graph JSON for adjacency and coordinates.',
          summary: 'Graph JSON editor state saved.',
          rowsAffected: [],
          snapshotDiff: buildGraphJsonSnapshotDiff(previousGraphJson, input),
          valueBefore: previousGraphJson,
          valueAfter: input,
        },
        ...prev,
      ])
    },
    [
      graphJsonSnapshot,
      onSaveGraphJsonEditorState,
      settingsSnapshot?.graphJson,
    ],
  )

  const seedKnowledgeMap = useCallback(
    async (input: SeedKnowledgeMapInput) => {
      if (!engineEnabled) return
      const payload = normalizeSeedInput(input)
      const seedResult = onSeedKnowledgeMap
        ? await onSeedKnowledgeMap(payload)
        : undefined
      const wasSeeded = knowledgeMapSeeded
      const prev = topicsRef.current
      const out = applyKnowledgeMapSeed(prev, payload)
      setTopics(out.topics)
      setPersistedSeedSubtopicStates(persistedSeedStatesFromTopics(out.topics))
      setKnowledgeMapSeeded(true)
      setLastAppliedSeedBucket(payload.bucket)
      setLastAppliedSeedAt(new Date())
      if (!onSeedKnowledgeMap) {
        setLastAppliedSeedCoachName('Coach')
      }
      if (wasSeeded || seedResult?.hasPendingProcessing) {
        setHasPendingSeedProcessing(true)
      }
      emitAuditEntry('coach_seed', {
        actor: 'Coach',
        subject: out.bucketLabel,
        rowsAffectedOverride: out.rowsAffected,
      })
    },
    [engineEnabled, emitAuditEntry, knowledgeMapSeeded, onSeedKnowledgeMap],
  )

  /**
   * Append-only revert: emits a `coach_revert` audit entry whose
   * `replacesEntryIds` points at the targeted entry. The original entry is
   * NEVER mutated — its "reverted" status is derived (`deriveEntryStatus`).
   * This is the foundation for the calculation log's "may never experience
   * a regression / no hallucinatory data" contract.
   */
  const deleteCalculation = useCallback(
    (entryId: string) => {
      if (!engineEnabled) return
      const original = calculationLog.find((e) => e.id === entryId)
      if (
        original?.reasonCode === 'graph_json_saved' &&
        original.sourceRowId === `CoachAdjustmentLog:${entryId}` &&
        onRevertGraphJsonEditorState
      ) {
        void onRevertGraphJsonEditorState(entryId).catch(() => {
          // Fall back to local audit-only revert marker below.
        })
      }
      // Revert is an audit-only record (one DB insert, no engine work) —
      // emit instantly without the "Recalculating…" banner.
      emitAuditEntry('coach_revert', {
        actor: 'Coach',
        subject: original?.summary ?? entryId,
        replacesEntryIds: [entryId],
      })
    },
    [
      engineEnabled,
      calculationLog,
      emitAuditEntry,
      onRevertGraphJsonEditorState,
    ],
  )

  /**
   * Append-only redo:
   * - If the targeted entry is currently reverted, this is a `coach_reapply`
   *   (the revert event stays in the log; a fresh applied entry is appended
   *   alongside).
   * - Otherwise this is a `reprocess_cascade` (kind=`replay`) — in production
   *   it would trigger a full replay of original attempt events under the
   *   current settings. In mock V1 we just append a single replay entry.
   *
   * Both routes flow through `runRecalculation` so the banner is shown
   * (matches existing UX — the coach asked for a recompute).
   */
  const redoCalculation = useCallback(
    (entryId: string) => {
      if (!engineEnabled) return
      const original = calculationLog.find((e) => e.id === entryId)
      const isReverted =
        original !== undefined &&
        deriveEntryStatus(original, calculationLog) === 'reverted'
      runRecalculation(isReverted ? 'coach_reapply' : 'reprocess_cascade', {
        actor: 'Coach',
        subject: original?.summary ?? entryId,
        replacesEntryIds: [entryId],
      })
    },
    [engineEnabled, calculationLog, runRecalculation],
  )

  const appendBaselineEpochLog = useCallback(
    (
      action: 'set' | 'update' | 'clear',
      previousEpochAt: Date | null,
      nextEpochAt: Date | null,
    ) => {
      const nowIso = new Date().toISOString()
      const payloadSummary =
        action === 'clear'
          ? 'Baseline epoch cleared — recommendation engine gated off for new work.'
          : action === 'set'
            ? 'Baseline epoch set — recommendation engine enabled for post-epoch work.'
            : 'Baseline epoch updated — recommendation engine boundary changed for future processing.'
      setCalculationLog((prev) => [
        {
          id: `calc_${crypto.randomUUID()}`,
          occurredAt: nowIso,
          source: 'coach_adjustment',
          kind: 'original',
          replacesEntryIds: [],
          sourceTable: 'CoachAdjustmentLog',
          sourceRowId: `CoachAdjustmentLog:baseline_epoch_${nowIso}`,
          actorKind: 'coach',
          actorName: 'Coach',
          reasonCode: 'baseline_epoch_change',
          rationale:
            'Coach changed the baseline epoch boundary used to gate recommendation engine processing.',
          summary: payloadSummary,
          rowsAffected: [
            {
              targetKind: 'overall',
              targetName: 'Recommendation Engine Gate',
              field: 'baselineEpochAt',
              before: previousEpochAt ? 1 : 0,
              after: nextEpochAt ? 1 : 0,
              formulaName: 'Baseline Epoch Gate',
              purpose:
                'Controls whether recommendation-engine mutations are enabled for this client.',
              equation: 'engineEnabled = baselineEpochAt != null',
              weights: [],
              inputs: [
                {
                  name: 'previousEpochAt',
                  value: previousEpochAt
                    ? previousEpochAt.toISOString()
                    : 'null',
                  note: 'epoch before this coach action',
                },
                {
                  name: 'newEpochAt',
                  value: nextEpochAt ? nextEpochAt.toISOString() : 'null',
                  note: 'epoch after this coach action',
                },
              ],
              output: `engineEnabled = ${nextEpochAt ? 'true' : 'false'}`,
            },
          ],
        },
        ...prev,
      ])
    },
    [],
  )

  const setBaselineEpoch = useCallback(
    async (date: string) => {
      if (!date) return
      setBaselineEpochBusy(true)
      const previous = baselineEpochAt
      try {
        if (onSetBaselineEpoch) {
          await onSetBaselineEpoch(date)
        } else {
          setBaselineEpochAtState(new Date(`${date}T00:00:00.000Z`))
        }
        appendBaselineEpochLog(
          'set',
          previous,
          new Date(`${date}T00:00:00.000Z`),
        )
      } finally {
        setBaselineEpochBusy(false)
      }
    },
    [appendBaselineEpochLog, baselineEpochAt, onSetBaselineEpoch],
  )

  const updateBaselineEpoch = useCallback(
    async (date: string) => {
      if (!date) return
      setBaselineEpochBusy(true)
      const previous = baselineEpochAt
      try {
        if (onUpdateBaselineEpoch) {
          await onUpdateBaselineEpoch(date)
        } else {
          setBaselineEpochAtState(new Date(`${date}T00:00:00.000Z`))
        }
        appendBaselineEpochLog(
          'update',
          previous,
          new Date(`${date}T00:00:00.000Z`),
        )
      } finally {
        setBaselineEpochBusy(false)
      }
    },
    [appendBaselineEpochLog, baselineEpochAt, onUpdateBaselineEpoch],
  )

  const clearBaselineEpoch = useCallback(async () => {
    setBaselineEpochBusy(true)
    const previous = baselineEpochAt
    try {
      if (onClearBaselineEpoch) {
        await onClearBaselineEpoch()
      } else {
        setBaselineEpochAtState(null)
      }
      appendBaselineEpochLog('clear', previous, null)
    } finally {
      setBaselineEpochBusy(false)
    }
  }, [appendBaselineEpochLog, baselineEpochAt, onClearBaselineEpoch])

  // --- derived selectors ---
  const topicsInPlanCount = useMemo(
    () => topics.filter((t) => t.includedInPlan).length,
    [topics],
  )
  const overallMastery = useMemo(() => computeOverallMastery(topics), [topics])
  const bucketCounts = useMemo(() => computeBucketCounts(topics), [topics])
  const planEstimate = useMemo(
    () => estimatePlanDuration(topics, planConfig.weeklyCommitmentHours),
    [topics, planConfig.weeklyCommitmentHours],
  )
  const projectedFinish = useMemo(
    () => (planEstimate ? projectedFinishDate(planEstimate.days) : null),
    [planEstimate],
  )
  const fitsIndicator = useMemo(
    () =>
      projectedFinish
        ? computeFitsIndicator(projectedFinish, planConfig.targetDate)
        : { status: 'none' as const, deltaDays: 0 },
    [projectedFinish, planConfig.targetDate],
  )
  const bucketOf = useCallback(
    (topicId: string): TopicBucket => {
      const topic = topics.find((t) => t.id === topicId)
      return topic ? classifyTopicBucket(topic) : 'remaining'
    },
    [topics],
  )
  const statusOf = useCallback(
    (entryId: string): CalculationStatus => {
      const entry = calculationLog.find((e) => e.id === entryId)
      if (!entry) return 'applied'
      return deriveEntryStatus(entry, calculationLog)
    },
    [calculationLog],
  )

  const pendingDiffState = useMemo<PendingDiffResult>(
    () =>
      derivePendingDiff(
        settingsSnapshot,
        {
          topics,
          plan: {
            weeklyCommitmentHours: planConfig.weeklyCommitmentHours,
            targetDate: planConfig.targetDate,
          },
          completedWork,
          recommendations: recommendationSnapshots,
          graphJson: graphJsonSnapshot,
        },
        { knowledgeMapSeeded, baselineEpochAt },
      ),
    [
      baselineEpochAt,
      completedWork,
      knowledgeMapSeeded,
      planConfig.targetDate,
      planConfig.weeklyCommitmentHours,
      recommendationSnapshots,
      graphJsonSnapshot,
      settingsSnapshot,
      topics,
    ],
  )
  const pendingDiffWithSeedProcessing = useMemo<PendingDiffResult>(() => {
    if (!hasPendingSeedProcessing) return pendingDiffState
    const filteredItems = pendingDiffState.items.filter(
      (item) => item.triggerActionType !== 'seed_values_change',
    )
    const filteredActions = pendingDiffState.triggerActions.filter(
      (action) => action !== 'seed_values_change',
    )
    const seedPendingDiff: DiffItem = {
      key: 'setup:seed-processing-pending',
      label: 'Seed knowledge map processing',
      tabId: 'setup',
      previousValue: 'not queued',
      currentValue: 'queued',
      triggerActionType: 'seed_values_change',
    }
    return {
      items: [...filteredItems, seedPendingDiff],
      triggerActions: [...filteredActions, seedPendingDiff.triggerActionType],
    }
  }, [hasPendingSeedProcessing, pendingDiffState])

  const dirtyTabs = useMemo(
    () => deriveDirtyTabs(pendingDiffWithSeedProcessing),
    [pendingDiffWithSeedProcessing],
  )
  const pendingProcessing = useMemo(
    () => summarizePendingDiffProcessing(pendingDiffWithSeedProcessing),
    [pendingDiffWithSeedProcessing],
  )
  const pendingProcessingSummary = pendingProcessing.summaryLabel
  const pendingProcessingTypes = pendingProcessing.processingTypes
  const canRevertToSnapshot = pendingDiffWithSeedProcessing.items.length > 0

  const triggerProcessingFromPendingChanges = useCallback(() => {
    if (pendingDiffWithSeedProcessing.items.length === 0) return
    const summary = summarizePendingProcessing(
      pendingDiffWithSeedProcessing.triggerActions,
    )
    const now = new Date()
    const snapshot = buildSnapshotState({
      topics,
      plan: {
        weeklyCommitmentHours: planConfig.weeklyCommitmentHours,
        targetDate: planConfig.targetDate,
      },
      completedWork,
      recommendations: recommendationSnapshots,
      graphJson: graphJsonSnapshot,
    })

    setSettingsSnapshot(snapshot)

    setCalculationLog((prev) => [
      {
        id: `calc_${crypto.randomUUID()}`,
        occurredAt: now.toISOString(),
        source: 'coach_adjustment',
        kind: 'original',
        replacesEntryIds: [],
        sourceTable: 'CoachAdjustmentLog',
        sourceRowId: `CoachAdjustmentLog:settings_snapshot_captured_${now.toISOString()}`,
        actorKind: 'coach',
        actorName: 'Coach',
        reasonCode: 'settings_snapshot_captured',
        rationale:
          'Snapshot of recommendation-engine settings was captured immediately after manual processing kickoff.',
        summary: 'Settings snapshot captured as new processing baseline.',
        rowsAffected: [],
        snapshotDiff: pendingDiffWithSeedProcessing.items.map((item) => ({
          label: item.label,
          previousValue: String(item.previousValue),
          currentValue: String(item.currentValue),
        })),
      },
      {
        id: `calc_${crypto.randomUUID()}`,
        occurredAt: now.toISOString(),
        source: 'coach_adjustment',
        kind: 'original',
        replacesEntryIds: [],
        sourceTable: 'CoachAdjustmentLog',
        sourceRowId: `CoachAdjustmentLog:processing_run_requested_${now.toISOString()}`,
        actorKind: 'coach',
        actorName: 'Coach',
        reasonCode: 'processing_run_requested',
        rationale:
          'Coach manually initiated recommendation-engine processing from pending changes.',
        summary: `Processing run requested: ${summary.summaryLabel}`,
        rowsAffected: [],
      },
      ...prev,
    ])
    if (hasPendingSeedProcessing && onRunPendingProcessing) {
      void onRunPendingProcessing()
        .then(() => {
          setHasPendingSeedProcessing(false)
        })
        .catch(() => {
          // Leave pending flag true so the coach can retry after a failed run.
        })
    } else if (
      pendingDiffWithSeedProcessing.triggerActions.includes(
        'graph_json_change',
      ) &&
      onRunPendingProcessing
    ) {
      void onRunPendingProcessing().catch(() => {
        // Leave pending graph diff visible so the coach can retry.
      })
    } else if (hasPendingSeedProcessing) {
      setHasPendingSeedProcessing(false)
    }
  }, [
    completedWork,
    hasPendingSeedProcessing,
    onRunPendingProcessing,
    pendingDiffWithSeedProcessing.items,
    pendingDiffWithSeedProcessing.triggerActions,
    planConfig.targetDate,
    planConfig.weeklyCommitmentHours,
    recommendationSnapshots,
    graphJsonSnapshot,
    topics,
  ])

  const revertToLastSnapshot = useCallback(() => {
    if (!settingsSnapshot) return
    const graphWasDirty = pendingDiffState.items.some(
      (item) => item.key === 'graph:json-editor-state',
    )
    setTopics(structuredClone(settingsSnapshot.topics))
    setPlanConfig(structuredClone(settingsSnapshot.plan))
    setCompletedWork((prev) =>
      prev.map((work) => {
        const snapshotWork = settingsSnapshot.completedWork.find(
          (item) => item.id === work.id,
        )
        if (!snapshotWork) return work
        return {
          ...work,
          needsGrading: snapshotWork.needsGrading,
          reportedApproach: snapshotWork.reportedApproachType
            ? {
                type: snapshotWork.reportedApproachType,
                isPreselected: work.knownApproaches.includes(
                  snapshotWork.reportedApproachType,
                ),
              }
            : null,
        }
      }),
    )
    setRecommendationSnapshots(
      structuredClone(settingsSnapshot.recommendations),
    )
    setGraphJsonSnapshot(structuredClone(settingsSnapshot.graphJson))
    if (
      graphWasDirty &&
      onRevertGraphJsonEditorState &&
      pendingGraphJsonAdjustmentIdRef.current
    ) {
      void onRevertGraphJsonEditorState(
        pendingGraphJsonAdjustmentIdRef.current,
      ).catch(() => {
        // Local snapshot was restored; backend revert can be retried from calc log.
      })
    }
    const nowIso = new Date().toISOString()
    setCalculationLog((prev) => [
      {
        id: `calc_${crypto.randomUUID()}`,
        occurredAt: nowIso,
        source: 'coach_adjustment',
        kind: 'original',
        replacesEntryIds: [],
        sourceTable: 'CoachAdjustmentLog',
        sourceRowId: `CoachAdjustmentLog:coach_revert_to_snapshot_${nowIso}`,
        actorKind: 'coach',
        actorName: 'Coach',
        reasonCode: 'coach_revert_to_snapshot',
        rationale:
          'Coach reverted recommendation-engine state to the latest captured snapshot.',
        summary:
          'Reverted current settings to latest processing snapshot baseline.',
        rowsAffected: [],
      },
      ...prev,
    ])
  }, [onRevertGraphJsonEditorState, pendingDiffState.items, settingsSnapshot])

  const value = useMemo<RecommendationEngineValue>(
    () => ({
      topics,
      planConfig,
      completedWork,
      recommendations,
      calculationLog,
      recalculating,
      engineEnabled,
      knowledgeMapSeeded,
      baselineEpochAt,
      baselineEpochTimeZone,
      baselineEpochBusy,
      pendingDiff: pendingDiffWithSeedProcessing.items,
      dirtyTabs,
      pendingProcessingTypes,
      pendingProcessingSummary,
      canRevertToSnapshot,
      catalogTopics,
      catalogTopicCount,
      catalogSubtopicCount,
      companyProficiencyPresets,
      lastAppliedSeedBucket,
      lastAppliedSeedAt,
      lastAppliedSeedCoachName,
      hasPendingSeedProcessing,
      persistedSeedSubtopicStates,
      graphJsonSnapshot,
      topicsInPlanCount,
      overallMastery,
      bucketCounts,
      planEstimate,
      projectedFinish,
      fitsIndicator,
      bucketOf,
      statusOf,
      toggleTopicInPlan,
      toggleSubtopicInPlan,
      setTopicMinThreshold,
      setTopicMaxThreshold,
      setSubtopicMinThreshold,
      setSubtopicMaxThreshold,
      setSubtopicRollupWeight,
      setWeeklyCommitment,
      setTargetDate,
      applyCompanyPreset,
      updateProficiencyThresholds: onUpdateProficiencyThresholds,
      updateReportedApproach,
      setRecommendationStatus,
      markReviewed,
      saveGraphJsonEditorState,
      seedKnowledgeMap,
      deleteCalculation,
      redoCalculation,
      setBaselineEpoch,
      updateBaselineEpoch,
      clearBaselineEpoch,
      triggerProcessingFromPendingChanges,
      revertToLastSnapshot,
    }),
    [
      topics,
      planConfig,
      completedWork,
      recommendations,
      calculationLog,
      recalculating,
      engineEnabled,
      knowledgeMapSeeded,
      baselineEpochAt,
      baselineEpochTimeZone,
      baselineEpochBusy,
      pendingDiffWithSeedProcessing.items,
      dirtyTabs,
      pendingProcessingTypes,
      pendingProcessingSummary,
      canRevertToSnapshot,
      catalogTopics,
      catalogTopicCount,
      catalogSubtopicCount,
      companyProficiencyPresets,
      lastAppliedSeedBucket,
      lastAppliedSeedAt,
      lastAppliedSeedCoachName,
      hasPendingSeedProcessing,
      persistedSeedSubtopicStates,
      graphJsonSnapshot,
      topicsInPlanCount,
      overallMastery,
      bucketCounts,
      planEstimate,
      projectedFinish,
      fitsIndicator,
      bucketOf,
      statusOf,
      toggleTopicInPlan,
      toggleSubtopicInPlan,
      setTopicMinThreshold,
      setTopicMaxThreshold,
      setSubtopicMinThreshold,
      setSubtopicMaxThreshold,
      setSubtopicRollupWeight,
      setWeeklyCommitment,
      setTargetDate,
      applyCompanyPreset,
      onUpdateProficiencyThresholds,
      updateReportedApproach,
      setRecommendationStatus,
      markReviewed,
      saveGraphJsonEditorState,
      seedKnowledgeMap,
      deleteCalculation,
      redoCalculation,
      setBaselineEpoch,
      updateBaselineEpoch,
      clearBaselineEpoch,
      triggerProcessingFromPendingChanges,
      revertToLastSnapshot,
    ],
  )

  return (
    <RecommendationEngineContext.Provider value={value}>
      {children}
    </RecommendationEngineContext.Provider>
  )
}

export function useRecommendationEngine(): RecommendationEngineValue {
  const ctx = useContext(RecommendationEngineContext)
  if (!ctx) {
    throw new Error(
      'useRecommendationEngine must be used within RecommendationEngineProvider',
    )
  }
  return ctx
}

// Backward-compatible aliases while tests migrate.
export type RecommendationEngineRuntimeValue = RecommendationEngineValue
export const RecommendationEngineRuntimeProvider = RecommendationEngineProvider
export const useRecommendationEngineRuntime = useRecommendationEngine
export type RecommendationEngineMockValue = RecommendationEngineValue
export function RecommendationEngineMockProvider({
  proficiencyGoalsTopics,
  initialCompletedWork,
  initialCalculationLog,
  initialRecommendations,
  ...props
}: React.ComponentProps<typeof RecommendationEngineProvider>) {
  return (
    <RecommendationEngineProvider
      {...props}
      proficiencyGoalsTopics={proficiencyGoalsTopics ?? MOCK_TOPICS}
      initialCompletedWork={initialCompletedWork ?? MOCK_COMPLETED_WORK}
      initialCalculationLog={initialCalculationLog ?? MOCK_CALCULATION_LOG}
      initialRecommendations={initialRecommendations ?? MOCK_RECOMMENDATIONS}
    />
  )
}
export const useRecommendationEngineMock = useRecommendationEngine
